import { KnowledgeItem, KnowledgeBase } from '../../../types';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';
import { assignRanks } from '../../../shared/utils/ranking';

export interface DocumentMetadata {
  key: string;
  title: string;
  description: string;
  order: number;
  size_bytes: number;
  last_modified: string;
}

export interface DocumentListResponse {
  documents: DocumentMetadata[];
  knowledge_base_id: string;
  prefix: string;
  total_count: number;
  total_size_bytes: number;
}

export interface DocumentContentResponse {
  key: string;
  content: string;
  metadata: Record<string, unknown>;
  title: string | null;
  description: string | null;
  order: number | null;
  size_bytes: number;
  last_modified: string;
  content_type: string;
  knowledge_base_id: string;
}

export const createKnowledgeService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    getKnowledgeBases: async (): Promise<{ knowledge_bases: KnowledgeBase[] }> => {
      return rpcCall<{ knowledge_bases: KnowledgeBase[] }>(client, 'knowledge.ListKnowledgeBases');
    },

    listDocuments: async (kbId: string, prefix?: string): Promise<DocumentListResponse> => {
      return rpcCall<DocumentListResponse>(client, 'knowledge.ListDocuments', { kb_id: kbId, prefix: prefix || '' });
    },

    getDocumentContent: async (kbId: string, documentPath: string): Promise<DocumentContentResponse> => {
      return rpcCall<DocumentContentResponse>(client, 'knowledge.GetDocumentContent', { kb_id: kbId, path: documentPath });
    },

    getKnowledgeForInvestigation: async (customerId: string, investigationId: string): Promise<KnowledgeItem[]> => {
      const result = await rpcCall<{ knowledge_items: KnowledgeItem[] }>(client, 'investigations.GetKnowledgeItems', { customer_id: customerId, investigation_id: investigationId });
      return result.knowledge_items || [];
    },

    updateKnowledgeRanking: async (
      customerId: string,
      investigationId: string,
      knowledgeItems: KnowledgeItem[]
    ): Promise<void> => {
      const rankedItems = assignRanks(knowledgeItems);
      await rpcCall(client, 'investigations.UpdateKnowledgeItemRanks', {
        customer_id: customerId,
        investigation_id: investigationId,
        knowledge_items: rankedItems.map(item => ({
          knowledge_base_id: item.knowledge_base_id,
          knowledge_item_id: item.knowledge_item_id,
          title: item.title,
          summary: item.summary,
          rank: item.rank,
        })),
      });
    },

    unlinkKnowledgeFromInvestigation: async (
      customerId: string,
      investigationId: string,
      knowledgeItemIds: string[]
    ): Promise<void> => {
      await rpcCall(client, 'investigations.DeleteKnowledgeItems', {
        customer_id: customerId,
        investigation_id: investigationId,
        knowledge_item_ids: knowledgeItemIds,
      });
    }
  };
};

export const useKnowledgeService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createKnowledgeService(idToken);
  }, [idToken]);
};